import { z } from 'zod';

// Formatting functions
export const formatCNPJ = (cnpj: string): string => {
  const numbers = cnpj.replace(/\D/g, '');
  return numbers.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
};

export const formatCPF = (cpf: string): string => {
  const numbers = cpf.replace(/\D/g, '');
  return numbers.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/, '$1.$2.$3-$4');
};

export const formatPhone = (phone: string): string => {
  const numbers = phone.replace(/\D/g, '');
  if (numbers.length === 11) {
    return numbers.replace(/^(\d{2})(\d{5})(\d{4})$/, '($1) $2-$3');
  }
  return numbers.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
};

export const formatCEP = (cep: string): string => {
  const numbers = cep.replace(/\D/g, '');
  return numbers.replace(/^(\d{5})(\d{3})$/, '$1-$2');
};

export const unformatDocument = (doc: string): string => {
  return doc.replace(/\D/g, '');
};

// Re-export all types and validation functions
export * from './user';
export * from './promotion';
export * from './company';
export * from './participant';
export * from './appointment';
export * from './assistant';
export * from './validation';

// Assistant-specific types
export interface Assistant {
  id: number;
  name: string;
  assistant_id: string;
  instructions: string;
  summary?: string;
  type: string;
  signature: string;
  integration_id: number | null;
  integration_description?: string;
  related_assistant_ids?: number[];
  created_at: string;
  updated_at: string;
}

export interface AssistantType {
  type: string;
  description: string;
}

export interface Integration {
  id: number;
  user_id?: number;
  description: string;
  ixc_url?: string;
  ixc_token?: string;
  gammu_user?: string;
  gammu_pwd?: string;
  ticketz_url?: string;
  ticketz_token?: string;
  ticketz_companyId?: number;
  ticketz_userId?: number;
  ixc_id_assunto?: number;
  ixc_id_tecnico?: number;
  ixc_id_atendente?: number;
  ixc_id_filial?: number;
  ixc_id_setor?: number;
  ixc_assunto_os_finished?: number[];
  ixc_assunto_os_new_contract?: number[];
  ixc_assunto_os_cancellation?: number[];
  ixc_assunto_os_budget?: number[];
  digisac_account?: string;
  digisac_url?: string;
  digisac_token?: string;
  sgp_url?: string;
  sgp_token?: string;
  sgp_app?: string;
  hubsoft_url?: string;
  hubsoft_client_id?: number;
  hubsoft_client_secret?: string;
  hubsoft_username?: string;
  hubsoft_password?: string;
  hubsoft_token?: string;
  sgp_ocorrenciatipo?: number;
  sgp_setor?: number;
  mediawiki_url?: string;
  mediawiki_username?: string;
  mediawiki_password?: string;
  opa_token?: string;
  opa_url?: string;
  opa_ssh_host?: string;
  opa_ssh_port?: number;
  opa_ssh_user?: string;
  opa_ssh_passwd?: string;
  hubsoft_id_tipo_atendimento?: number;
  fortics_url?: string;
  fortics_user?: string;
  fortics_password?: string;
  fortics_token?: string;
  asaas_url?: string;
  asaas_access_token?: string;
  asaas_evp_key?: string;
  generic_url?: string;
  generic_token?: string;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppSession {
  id: number;
  session_name: string;
  status: string;
  qr_code?: string;
  assistant_id: number | null;
  assistant_name?: string;
  isDefault: boolean;
  token?: string;
  engine?: 'GMAIL' | 'TICKETZ' | 'SMTP' | 'DIGISAC' | 'GOWS' | 'OPA' | 'INTERNAL' | 'TWILLIO' | 'FORTICS';
  send_alert_at?: string;
  smtp_host?: string;
  smtp_port?: number;
  smtp_username?: string;
  smtp_password?: string;
  smtp_secure?: boolean;
  channel?: string;
  messages_per_minute?: number;
  messages_per_hour?: number;
  messages_time_condition_id?: number | null;
  voice_number?: string;
  integration_id?: number | null;
  created_at: string;
  updated_at: string;
}

export interface QRCodeResponse {
  url?: string;
  status: string;
  message?: string;
}

export interface RegisterRequest {
  username: string;
  password: string;
  confirmPassword?: string;
  name: string;
  person_type: 'fisica' | 'juridica';
  cnpj_cpf: string;
  email?: string;
  phone?: string;
  address?: string;
  number?: string;
  district?: string;
  city?: string;
  zip_code?: string;
  profile_id?: number;
  api_key?: string;
  plan_id?: number;
  affiliate_code?: string;
}

export interface AuthResponse {
  success: boolean;
  message?: string;
  requiresTwoFactor?: boolean;
  data?: {
    token: string;
    refreshToken: string;
    user: User;
  } | {
    twoFactorToken: string;
    user: {
      id: number;
      username: string;
      name: string;
      isChildAccount: boolean;
    };
  };
}

export interface TwoFactorSetupResponse {
  success: boolean;
  data?: {
    secret: string;
    otpauthUrl: string;
  };
  message?: string;
}

export interface TwoFactorStatusResponse {
  success: boolean;
  data?: {
    enabled: boolean;
  };
}

export interface TwoFactorLoginResponse {
  success: boolean;
  message?: string;
  data?: {
    token: string;
    refreshToken: string;
    user: User;
  };
}
